import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
} from '@angular/router';
import { filter, map, take } from 'rxjs/operators';

import { AuthService } from '@app/core/services/auth.service';
import { BlockModalService } from '@app/core/services/block-modal.service';

/**
 * CB — Client Blocking (2026-06-30, AD-049 / CB-LD-9 / CB-LD-19).
 *
 * Blocks navigation to the `transfers` and `withdrawals` route groups
 * (under both personal and corporate trees) when the user's
 * `blockStatus === 'transfer_withdraw_blocked'`. All other routes
 * remain accessible — the user retains read-only and account-management
 * functionality.
 *
 * The guard is applied at the parent of each transfer/withdrawal route
 * group, so it covers `/transfers`, `/transfers/:id`, `/withdrawals`,
 * `/withdrawals/:id`, and their `/corporate/...` mirrors via the same
 * single placement on each parent.
 *
 * On reject: opens the consent modal via `BlockModalService.open(...)`
 * and returns `false` (stays on the current page).
 *
 * `none`, `blocked`, and `partially_blocked` users are passed through
 * unchanged — those statuses are handled by other guards / the login
 * pre-token gate.
 */
export const transferWithdrawBlockGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const blockModalService = inject(BlockModalService);

  const evaluate = (): boolean => {
    const user = authService.getUser();
    if (!user) return true; // defer to authGuard
    if (user.blockStatus !== 'transfer_withdraw_blocked') return true;

    blockModalService.open('transfer_withdraw_blocked', user.blockReason);
    return false;
  };

  if (authService.restoreInProgress()) {
    return toObservable(authService.restoreInProgress).pipe(
      filter((inProgress) => !inProgress),
      take(1),
      map(() => evaluate()),
    );
  }

  return evaluate();
};
